/**
 * The creator's platforms, in a stable order, with fresh live status on top.
 *
 * Two lists, because they answer two questions: what the creator has CONFIGURED
 * (ordered by `STREAM_PRECEDENCE` so the source toggle never reshuffles under
 * the viewer), and what is live RIGHT NOW (the same list with the live-status
 * query overlaid).
 *
 * Split out of `CreatorSection` for the 400-line ceiling — this is the one
 * derivation in that file with a real rule in it, and it reads better with the
 * rule stated at the top rather than sixty lines into a component.
 */
import { useMemo } from "react";
import type { FeaturedStreamResponse, StreamingSettings } from "@/hooks/hub/useGetHub";
import { STREAM_PRECEDENCE } from "../shared";

export interface CreatorPlatforms {
    configured: FeaturedStreamResponse[];
    available: FeaturedStreamResponse[];
}

export function useCreatorPlatforms(
    streamingSettings: StreamingSettings | undefined,
    liveByPlatform: Record<string, { isLive: boolean; viewerCount: number }>,
): CreatorPlatforms {
    const featuredStreams = streamingSettings?.featuredStreams ?? [];
    const configuredPlatforms: FeaturedStreamResponse[] = useMemo(() => {
        const seen = new Set<string>();
        const result: FeaturedStreamResponse[] = [];
        for (const p of STREAM_PRECEDENCE) {
            /*
             * THE CHANNEL DECIDES THE PLATFORM, NOT THE `platform` FIELD.
             *
             * This required `s.platform === p` AND the matching channel id. A
             * row holding a perfectly good `twitchChannelId` was skipped
             * whenever `platform` disagreed with it or was unset — the Drop's
             * own channel then got no player at all, which is the defect
             * tracked as "a Drop's own Twitch/Kick/YouTube channel never gets a
             * player, only CREATOR pov does".
             *
             * Two ways a row reaches that state. It has always been possible
             * for `platform` to be stamped wrong: the Drop-assignment path in
             * `add_team_member.ts` writes `primary?.platform ?? TWITCH`, so a
             * creator whose Drop carried only a Kick slug was labelled TWITCH
             * and matched nothing. And `platform` is now legitimately OPTIONAL
             * (a member is not required to have a channel), so a row can carry
             * a channel with no platform beside it at all.
             *
             * A channel id IS the platform — a `kickChannelSlug` cannot be
             * anything but Kick. Matching on the channel makes the declared
             * field a hint rather than a gate, so the player follows the data
             * that can actually be played.
             */
            const found = featuredStreams.find(s => !seen.has(p) && (
                p === "TWITCH" && !!s.twitchChannelId ||
                p === "KICK" && !!s.kickChannelSlug ||
                p === "YOUTUBE" && (!!s.youtubeChannelId || !!s.youtubeLiveId)
            ));
            if (found) {
                /*
                 * Carry the platform the CHANNEL proves, so everything
                 * downstream — the source toggle, `liveByPlatform`, the panel —
                 * keys on the same answer instead of re-deriving it or reading
                 * a stale `platform` back off the row.
                 */
                seen.add(p); result.push({...found, platform: p});
            }
        }
        return result;
    }, [featuredStreams]);

    const availablePlatforms: FeaturedStreamResponse[] = useMemo(() => {
        return configuredPlatforms.map(s => {
            /*
             * Safe to index: every row in `configuredPlatforms` was stamped
             * with the platform its CHANNEL proves, just above — so `platform`
             * is set here even though it is optional on the underlying type.
             */
            const fresh = s.platform ? liveByPlatform[s.platform] : undefined;
            if (!fresh) return s;
            return { ...s, isLive: fresh.isLive, viewerCount: fresh.viewerCount };
        });
    }, [configuredPlatforms, liveByPlatform]);

    return {configured: configuredPlatforms, available: availablePlatforms};
}
